import { Badge, Tooltip } from '@iveco-ti/iveco-ds'
import { FileEdit, Trash } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  ApplicationAccountModel,
  ApplicationAccountStatus,
} from '@/common/models/applicationAccount'
import { useAllApplications } from '../../../../../common/hooks/useAllApplications'
import { useInactivateApplicationAccount } from '../hooks/useInactivateApplicationAccount'

type LinkedApplicationAccountProps = {
  application: ApplicationAccountModel
  onEdit: (applicationAccount: ApplicationAccountModel) => void
}

export function LinkedApplicationAccount({
  application,
  onEdit,
}: LinkedApplicationAccountProps) {
  const { t } = useTranslation(['sso', 'common'])
  const { applications } = useAllApplications()
  const { handleInactivateApplicationAccount } =
    useInactivateApplicationAccount()

  const TITLE = applications.find(
    (option) => option.id === application.applicationId,
  )?.title

  if (!TITLE) return null

  return (
    <div className="grid grid-cols-5 items-center justify-center gap-4 border border-base-200 rounded-2xl px-5 py-2.5">
      <span className="text-neutral-800 font-medium text-sm">{TITLE}</span>

      {application.isMainAccount ? (
        <Badge.Root colors="success" className="w-fit" size="sm">
          {t(
            'sso:accounts.drawer_manage_applications.linked_application.isMainAccount',
          )}
        </Badge.Root>
      ) : (
        <div />
      )}

      <div className="w-fit">
        {application.status === ApplicationAccountStatus.PENDING && (
          <Badge.Root colors="warning" shape="square" size="sm">
            {t(
              'sso:accounts.drawer_manage_applications.linked_application.status_pending',
            )}
          </Badge.Root>
        )}
      </div>

      <div className="flex flex-wrap gap-1 items-center justify-center">
        {application.tag.map((tag) => (
          <Badge.Root key={tag} shape="square" size="sm">
            {tag}
          </Badge.Root>
        ))}
      </div>

      <div className="flex gap-2 justify-end">
        <Tooltip text={t('common:components.table_row.edit')}>
          <button
            type="button"
            className="p-1 text-brand-700 hover:bg-base-100 rounded"
            onClick={() => onEdit(application)}
          >
            <FileEdit size={18} />
          </button>
        </Tooltip>

        <Tooltip text={t('common:components.table_row.remove')}>
          <button
            type="button"
            className="p-1 text-danger-700 hover:bg-base-100 rounded"
            onClick={() => handleInactivateApplicationAccount(application.id)}
          >
            <Trash size={18} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}
